import { useState } from 'react';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ContactForm from '@/components/base/ContactForm';
import { faqs, siteMeta, testimonials } from '@/mocks/site';
import useSeo from '@/hooks/useSeo';

const principles = [
  {
    icon: 'ri-shield-check-line',
    title: '两次独立核实',
    desc: '每条爆料至少经过两次独立信源比对，无法确认的部分明确标注为待验证。',
  },
  {
    icon: 'ri-user-unfollow-line',
    title: '匿名化表述',
    desc: '在信息未公开前统一使用匿名表达，只讨论事件逻辑，不对个人下定性结论。',
  },
  {
    icon: 'ri-scales-3-line',
    title: '只讲可查证的部分',
    desc: '不渲染情绪化的细节，把有限的篇幅留给可以追溯的行业事实。',
  },
  {
    icon: 'ri-refresh-line',
    title: '错了就改',
    desc: '收到有效纠错后，我们会在原文标注更正说明，而不是悄悄修改后当作没发生。',
  },
];

const milestones = [
  { value: '2019', label: '开始持续更新' },
  { value: '1,200+', label: '累计独家条目' },
  { value: '6', label: '固定内容栏目' },
  { value: '两轮', label: '信源核实流程' },
];

const About = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useSeo({
    title: '关于我们 - 看瓜爆料的定位与内容原则',
    description:
      '了解看瓜爆料的品牌定位、发展理念、内容核实原则与编辑团队。我们坚持独家一手私料与匿名化表述，只发布可追溯的娱乐行业内幕。',
    keywords: '看瓜爆料,关于我们,内容原则,信源核实,娱乐资讯门户',
    path: '/about',
    schema: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于看瓜爆料',
        inLanguage: 'zh-CN',
        description: '看瓜爆料的品牌定位、发展理念与内容原则说明。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: siteMeta.name,
        email: siteMeta.email,
        foundingDate: siteMeta.since,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: '/about' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  });

  return (
    <SiteLayout>
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

      <section className="border-b border-background-200/70 bg-gradient-to-br from-background-50 via-background-100 to-secondary-50">
        <div className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
            <i className="ri-team-line text-sm"></i>
            关于我们
          </span>
          <h1 className="mt-4 max-w-3xl font-heading text-3xl font-bold leading-tight tracking-tight text-foreground-950 md:text-4xl">
            把娱乐新闻放回它真实的结构里
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {siteMeta.intro}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
          <div className="overflow-hidden rounded-lg border border-background-200/70">
            <div className="h-64 w-full overflow-hidden md:h-[360px]">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20editorial%20newsroom%20desk%20with%20notebooks%20coffee%20and%20soft%20window%20light%2C%20cinematic%20photography%20with%20amber%20and%20green%20accents%2C%20soft%20film%20grain&width=900&height=700&seq=kg-about-01&orientation=landscape"
                alt="看瓜爆料编辑团队的选题与核实工作场景"
                title="看瓜爆料 - 编辑团队工作场景"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              我们在做什么
            </h2>
            <div className="mt-4 space-y-4 text-sm leading-8 text-foreground-700 md:text-base">
              <p>
                看瓜爆料从 {siteMeta.since} 年开始持续更新，专注娱乐行业的动态与幕后信息。
                我们不追逐未经证实的传闻，而是把选题放在「这件事为什么会发生」上——
                从排播节奏到商务结构，从录制流程到分成比例。
              </p>
              <p>
                平台上大量娱乐内容停留在「发生了什么」，我们更想补上「背后是怎么运作的」。
                这也是我们坚持采访式写作、坚持信源交叉核实的原因。
              </p>
            </div>
            <dl className="mt-7 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {milestones.map((item) => (
                <div key={item.label} className="rounded-md border border-background-200/70 bg-background-100/50 px-4 py-3">
                  <dt className="font-heading text-lg font-bold text-primary-600">{item.value}</dt>
                  <dd className="mt-1 text-xs text-foreground-500">{item.label}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <section className="border-y border-background-200/70 bg-background-100/40 py-12 md:py-16">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
            <i className="ri-bookmark-3-line text-sm"></i>
            内容原则
          </span>
          <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            我们给自己定的四条规矩
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {principles.map((item) => (
              <div
                key={item.title}
                className="flex items-start gap-4 rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
          <i className="ri-chat-quote-line text-sm"></i>
          读者怎么说
        </span>
        <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          来自行业里和圈外的声音
        </h2>
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
          {testimonials.map((item) => (
            <figure
              key={item.name}
              className="flex h-full flex-col rounded-lg border border-background-200/70 bg-background-100/50 p-5 md:p-6"
            >
              <i className="ri-double-quotes-l text-2xl text-primary-400"></i>
              <blockquote className="mt-2 flex-1 text-sm leading-relaxed text-foreground-700">
                {item.content}
              </blockquote>
              <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200/70 pt-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-200 text-sm font-bold text-secondary-900">
                  {item.name.slice(0, 1)}
                </span>
                <span>
                  <span className="block text-sm font-medium text-foreground-950">{item.name}</span>
                  <span className="block text-xs text-foreground-500">{item.role}</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="border-y border-background-200/70 bg-background-100/40 py-12 md:py-16" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                <i className="ri-question-line text-sm"></i>
                常见问题
              </span>
              <h2 id="faq-heading" className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                你可能想先弄清楚的事
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                关于内容来源、表述方式与订阅，这些是我们被问得最多的问题。还有别的疑问，可以直接在下方留言。
              </p>
            </div>

            <div className="divide-y divide-background-200/70 rounded-lg border border-background-200/70 bg-background-50">
              {faqs.map((item, index) => {
                const isOpen = openFaq === index;
                return (
                  <div key={item.q}>
                    <h3>
                      <button
                        type="button"
                        onClick={() => setOpenFaq(isOpen ? null : index)}
                        aria-expanded={isOpen}
                        className="flex w-full cursor-pointer items-center justify-between gap-4 px-5 py-4 text-left md:px-6"
                      >
                        <span className="text-sm font-medium text-foreground-950 md:text-base">{item.q}</span>
                        <i
                          className={`shrink-0 text-xl text-primary-600 transition-transform ${
                            isOpen ? 'ri-subtract-line' : 'ri-add-line'
                          }`}
                        ></i>
                      </button>
                    </h3>
                    {isOpen && (
                      <div className="px-5 pb-5 md:px-6">
                        <p className="text-sm leading-relaxed text-foreground-600">{item.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section id="contact" className="mx-auto max-w-content scroll-mt-20 px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
              <i className="ri-mail-open-line text-sm"></i>
              联系我们
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              有线索，或者想聊聊？
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              无论是提供爆料线索、内容纠错，还是转载授权与商务合作，都可以通过下面的表单直接联系编辑团队。
            </p>
            <ul className="mt-6 space-y-3 text-sm text-foreground-700">
              <li className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-primary-600">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                {siteMeta.email}
              </li>
              <li className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-primary-600">
                  <i className="ri-time-line text-lg"></i>
                </span>
                工作日 10:00 - 19:00 回复
              </li>
              <li className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-primary-600">
                  <i className="ri-shield-keyhole-line text-lg"></i>
                </span>
                线索信息严格保密，可匿名提供
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-background-200/70 bg-background-100/50 p-5 md:p-8">
            <ContactForm />
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default About;